import React, { useState, useEffect } from 'react';
import { TrendingUp, Wallet, Eye, EyeOff, RefreshCw, Clock } from 'lucide-react';
import CryptoIcon from './CryptoIcon';
import { useBalance } from '../contexts/BalanceContext';
import { useBalanceVisibility } from '../contexts/BalanceVisibilityContext';

const HoldingsDisplay = ({ 
  showTotal = true, 
  showIndividual = true,
  className = '',
  compact = false,
  showLastUpdate = true,
  showRefreshButton = true,
  title = "Total Portfolio Value"
}) => {
  const { holdings, totalBalanceUSDT, balances, balanceData, loading, error, isUsingLivePrices, refreshBalance, lastUpdated, isAuthenticated, hasError } = useBalance();
  const { balanceVisible, toggleBalanceVisibility, formatBalance } = useBalanceVisibility();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [timeAgo, setTimeAgo] = useState('');

  // Calculate time ago for last update
  useEffect(() => {
    if (!lastUpdated) return;

    const updateTimeAgo = () => {
      try {
        const lastUpdateTime = new Date(lastUpdated);
        const now = new Date();
        const diffInSeconds = Math.floor((now - lastUpdateTime) / 1000);
        
        if (diffInSeconds < 60) {
          setTimeAgo(`${diffInSeconds}s ago`);
        } else if (diffInSeconds < 3600) {
          setTimeAgo(`${Math.floor(diffInSeconds / 60)}m ago`);
        } else {
          setTimeAgo(`${Math.floor(diffInSeconds / 3600)}h ago`);
        }
      } catch (error) {
        setTimeAgo('');
      }
    };

    updateTimeAgo();
    const interval = setInterval(updateTimeAgo, 10000); // Update every 10 seconds
    return () => clearInterval(interval);
  }, [lastUpdated]);

  // Handle manual refresh
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refreshBalance();
    setTimeout(() => setIsRefreshing(false), 1000);
  };

  if (loading && !balanceData) {
    return (
      <div className={`${className}`}>
        <div className="bg-gradient-to-r from-cyan-900/30 to-blue-900/30 border border-cyan-500/30 rounded-xl p-6">
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-cyan-400 border-t-transparent mb-4"></div>
              <p className="text-cyan-400 font-semibold">Loading your balance...</p>
              <p className="text-cyan-600 text-sm mt-2">Fetching live cryptocurrency prices</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if there's an error and no data AND user is authenticated
  if (hasError && !balanceData && isAuthenticated) {
    return (
      <div className={`${className}`}>
        <div className="bg-gradient-to-r from-orange-900/30 to-yellow-900/30 border border-orange-500/30 rounded-xl p-6">
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <RefreshCw className="w-12 h-12 text-orange-400 mx-auto mb-4" />
              <p className="text-orange-400 font-semibold mb-2">Unable to Load Balance</p>
              <p className="text-orange-300 text-sm mb-4">{error || 'Please try refreshing'}</p>
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="px-6 py-2 bg-orange-500/20 hover:bg-orange-500/30 border border-orange-500/50 rounded-lg text-orange-300 font-semibold transition-all disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 inline mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                {isRefreshing ? 'Refreshing...' : 'Refresh Balance'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If not authenticated and no data, show loading state briefly then empty
  if (!isAuthenticated && !balanceData) {
    // Check token again - might be a timing issue
    const token = localStorage.getItem('token');
    if (token) {
      // Token exists but isAuthenticated is false - trigger refresh
      setTimeout(() => refreshBalance(), 500);
      return (
        <div className={`${className}`}>
          <div className="bg-gradient-to-r from-cyan-900/30 to-blue-900/30 border border-cyan-500/30 rounded-xl p-6">
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-cyan-400 border-t-transparent mb-4"></div>
                <p className="text-cyan-400 font-semibold">Loading your balance...</p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className={`${className}`}>
        <div className="bg-gradient-to-r from-slate-800/50 to-slate-900/50 border border-slate-600/30 rounded-xl p-6">
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <Wallet className="w-12 h-12 text-slate-500 mx-auto mb-4" />
              <p className="text-slate-400 font-semibold">Portfolio</p>
              <p className="text-slate-500 text-sm mt-2">Login to view your balance</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Total Balance Card */}
      {showTotal && (
        <div className="bg-gradient-to-r from-cyan-900/30 to-blue-900/30 border border-cyan-500/30 rounded-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-cyan-400" />
              <span className="text-sm text-gray-400">{title}</span>
            </div>
            <div className="flex items-center gap-2">
              {showRefreshButton && (
                <button
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  className={`p-2 hover:bg-cyan-500/10 rounded-lg transition-colors ${isRefreshing ? 'animate-spin' : ''}`}
                  title="Refresh balance"
                >
                  <RefreshCw className="w-4 h-4 text-cyan-400" />
                </button>
              )}
              <button
                onClick={toggleBalanceVisibility}
                className="p-2 hover:bg-cyan-500/10 rounded-lg transition-colors"
                title={balanceVisible ? "Hide balance" : "Show balance"}
              >
                {balanceVisible ? 
                  <Eye className="w-4 h-4 text-cyan-400" /> : 
                  <EyeOff className="w-4 h-4 text-gray-400" />
                }
              </button>
            </div>
          </div>
          
          <div className="text-3xl font-bold text-white mb-2">
            {balanceVisible ? `$${totalBalanceUSDT.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '****'}
          </div>
          
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-3">
              {isUsingLivePrices() && (
                <div className="flex items-center gap-1 text-green-400">
                  <TrendingUp className="w-3 h-3" />
                  <span>Live prices via Tatum API</span>
                </div>
              )}
              {holdings.length > 0 && (
                <div className="text-gray-400">
                  {holdings.length} {holdings.length === 1 ? 'asset' : 'assets'}
                </div>
              )}
            </div>
            {showLastUpdate && timeAgo && (
              <div className="flex items-center gap-1 text-gray-500">
                <Clock className="w-3 h-3" />
                <span>{timeAgo}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Individual Holdings */}
      {showIndividual && holdings.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm text-gray-400 font-semibold">
              Your Holdings ({holdings.length} {holdings.length === 1 ? 'Asset' : 'Assets'})
            </div>
            <div className="text-xs text-gray-500">
              Sorted by value
            </div>
          </div>
          
          {holdings.map((holding, index) => {
            const percentage = totalBalanceUSDT > 0 ? (holding.value_usd / totalBalanceUSDT * 100).toFixed(1) : 0;
            
            return (
              <div
                key={holding.currency}
                className={`bg-slate-800/50 border border-slate-700 rounded-lg p-4 hover:border-cyan-500/30 transition-all ${
                  compact ? 'p-3' : ''
                } ${index === 0 ? 'border-cyan-500/50' : ''}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-full flex items-center justify-center relative">
                      <CryptoIcon symbol={holding.currency} className="w-6 h-6" />
                      {index === 0 && totalBalanceUSDT > 0 && (
                        <div className="absolute -top-1 -right-1 w-5 h-5 bg-cyan-500 rounded-full flex items-center justify-center text-[10px] font-bold">
                          1
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="font-semibold text-white flex items-center gap-2">
                        {(holding.currency || '').toUpperCase()}
                        {index === 0 && totalBalanceUSDT > 0 && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-cyan-500/20 text-cyan-400 rounded">
                            Top holding
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-400">
                        {balanceVisible ? 
                          `${holding.amount.toFixed(8)} ${(holding.currency || '').toUpperCase()}` : 
                          '****'
                        }
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-cyan-400">
                      {balanceVisible ? 
                        `$${holding.value_usd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 
                        '****'
                      }
                    </div>
                    <div className="text-xs text-gray-400">
                      @ ${holding.rate_usd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                </div>
                
                {/* Percentage bar */}
                {balanceVisible && totalBalanceUSDT > 0 && (
                  <div className="mt-2">
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                      <span>Portfolio allocation</span>
                      <span>{percentage}%</span>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-1.5 overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {showIndividual && holdings.length === 0 && (
        <div className="text-center py-12 bg-slate-800/30 rounded-xl border border-slate-700 border-dashed">
          <Wallet className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400">No holdings yet</p>
          <p className="text-sm text-gray-500 mt-1">Start trading to build your portfolio</p>
        </div>
      )}
    </div>
  );
};

export default HoldingsDisplay;
